import {
  Text,
  GreyGrey40,
  GreyGrey50,
  GreyGrey60,
  GreyGrey70,
  GreyGrey90,
  Primary13Regular,
  Primary20SemiBold,
  Card,
  InfoPopover,
  Skeleton,
  XYChart,
  IconCircleInfo,
  Box,
  Flex
} from '@sequencehq/core-components'
import { toCurrencySymbol, toMoney } from '@sequencehq/utils'
import { ChartTooltip } from 'RevenueRecognition/view/common/ChartTooltip'
import { format, formatDateRange } from '@sequencehq/utils/dates'
import { calculateYAxisLabels } from 'RevenueRecognition/view/utils/graphUtils'
import { capitalize } from 'inflection'
import { OVERVIEW_CARD_HEIGHT } from '../../constants'
import { Currency } from '@sequencehq/api/commonEnums'

const GraphDataTooltip = ({
  type,
  date,
  value,
  currency
}: {
  type: RevenueChartType
  date: string
  value: number
  currency: Currency
}) => {
  return (
    <ChartTooltip>
      <ChartTooltip.Header>
        <Text color={GreyGrey70}>{format(new Date(date), 'MMMM yyyy')}</Text>
      </ChartTooltip.Header>
      <ChartTooltip.Body>
        <Flex width="100%" justifyContent="space-between">
          <Text color={GreyGrey70}>{capitalize(type)}</Text>
          <Text color={GreyGrey90}>
            {toMoney({ currency, value: value.toString() })}
          </Text>
        </Flex>
      </ChartTooltip.Body>
    </ChartTooltip>
  )
}

type RevenueChartType = 'recognized' | 'deferred'

interface OverviewRevenueBarChartCardProps {
  type: RevenueChartType
  isLoading: boolean
  total: number
  chartData: Array<{ date: string; value: number }>
  currency: Currency
  dateRange: [Date, Date]
}

export const OverviewRevenueBarChartCard = ({
  type,
  isLoading,
  total,
  chartData,
  currency,
  dateRange
}: OverviewRevenueBarChartCardProps) => {
  if (isLoading) {
    return (
      <Skeleton
        width="400px"
        height={OVERVIEW_CARD_HEIGHT}
        borderRadius="8px"
      />
    )
  }

  return (
    <Card
      height={OVERVIEW_CARD_HEIGHT}
      width="400px"
      data-testid={`revrec.overview.${type}RevenueCard`}
      borderRadius="8px"
      border={`1px solid ${GreyGrey40}`}
      boxShadow="0px 0px 1px rgba(0, 0, 0, 0.02), 0px 1px 1px rgba(0, 0, 0, 0.05)"
      padding="0"
      overflow="hidden"
    >
      <Flex
        padding="16px"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Flex flexDirection="column" gap="8px" minWidth="200px">
          <Text
            {...Primary13Regular}
            fontWeight="semibold"
            color={GreyGrey60}
            gap="4px"
            display="flex"
            alignItems="center"
          >
            {capitalize(type)} revenue
            {type === 'deferred' && (
              <InfoPopover
                variant="info"
                minimal
                padding="0"
                placement="top"
                body={
                  <Box width={300} p="4px">
                    <Text {...Primary13Regular} color={GreyGrey70}>
                      This chart shows the running balance of deferred revenue
                      at the end of each month.
                    </Text>
                  </Box>
                }
                icon={<IconCircleInfo color={GreyGrey50} />}
              />
            )}
          </Text>
          <Text {...Primary20SemiBold} color={GreyGrey90}>
            {toMoney({
              currency,
              value: total.toString()
            })}
          </Text>
          <Text {...Primary13Regular} color={GreyGrey60}>
            {dateRange
              ? formatDateRange({
                  from: dateRange[0],
                  to: dateRange[1],
                  separator: ' - '
                })
              : ''}
          </Text>
        </Flex>
      </Flex>

      <Box height="200px" px="16px" pb="10px" pt="8px">
        {chartData.length === 0 ? (
          <Flex
            width="100%"
            height="100%"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap="8px"
          >
            <Text {...Primary13Regular} color={GreyGrey60}>
              No revenue data available
            </Text>
          </Flex>
        ) : (
          <Box
            width="100%"
            height="100%"
            overflow="hidden"
            px="0"
            data-testid={`revrec.overview.${type}RevenueChart`}
          >
            <XYChart<{
              value: number
              date: string
            }>
              variant="bar"
              dateFrom={chartData[0]?.date}
              dateTo={chartData[chartData.length - 1]?.date}
              data={chartData}
              dateFormat="MMM yyyy"
              unitTickValues={calculateYAxisLabels(
                chartData.map(({ value }) => value)
              )}
              unit={toCurrencySymbol(currency)}
              animate={false}
              showAllDateTicks={false}
              renderTooltip={({ date, value }) => (
                <GraphDataTooltip
                  type={type}
                  date={date}
                  value={value}
                  currency={currency}
                />
              )}
            />
          </Box>
        )}
      </Box>
    </Card>
  )
}
